import { useDispatch } from "react-redux";
import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Icon } from "@iconify/react";
import { CircularProgress, IconButton, useTheme } from "@mui/material";
import {
  addWishlist,
  deleteWishlist,
  getWishlist,
} from "@app/modules/wishlist/slice";
import {
  selectAddWishlistProducts,
  selectDeleteWishlistProducts,
  selectGetWishlistProducts,
} from "@app/modules/wishlist/selector";
import { useRequestState } from "@app/hooks";

const ToggleWishlist = ({ vehicle }: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const singleLoading = useRef<null | number>(null);

  const handleLoadingState = useCallback(() => {
    singleLoading.current = null;
  }, []);

  const { data: wishlistData, loading: getLoading } = useRequestState({
    stateSelector: selectGetWishlistProducts,
    onSuccess: handleLoadingState,
  });
  const { loading: addLoading } = useRequestState({
    stateSelector: selectAddWishlistProducts,
    onSuccess: handleLoadingState,
  });
  const { loading: deleteLoading } = useRequestState({
    stateSelector: selectDeleteWishlistProducts,
    onSuccess: handleLoadingState,
  });

  const isInWishlist = useMemo(() => {
    const items =
      wishlistData?.data?.data || wishlistData?.data || wishlistData;
    if (!Array.isArray(items)) return false;
    return items.some((item: any) => item.car?.id === vehicle?.id);
  }, [wishlistData, vehicle?.id]);

  const [localChecked, setLocalChecked] = useState(isInWishlist);
  const [clicked, setClicked] = useState(false);

  useEffect(() => {
    setLocalChecked(isInWishlist);
  }, [isInWishlist]);

  const handleChecked = () => {
    if (addLoading || deleteLoading || getLoading) return;

    setClicked(true);
    singleLoading.current = vehicle?.id;

    if (!localChecked) {
      dispatch(addWishlist({ carId: vehicle?.id }));
    } else {
      dispatch(deleteWishlist({ carId: vehicle?.id }));
    }
  };

  useEffect(() => {
    if (!clicked) return;
    if (!addLoading && !deleteLoading) {
      dispatch(getWishlist({}));
      setClicked(false);
      singleLoading.current = null;
    }
  }, [addLoading, deleteLoading, clicked, dispatch]);

  const loading =
    singleLoading.current === vehicle?.id &&
    (addLoading || deleteLoading || getLoading);

  return (
    <IconButton disableRipple onClick={handleChecked} disabled={loading}>
      {loading ? (
        <CircularProgress size={20} />
      ) : (
        <Icon
          icon={localChecked ? "mdi:heart" : "mdi:heart-outline"}
          color={
            localChecked
              ? theme.palette.primary.main
              : theme.palette.common.black
          }
          width={24}
          height={24}
        />
      )}
    </IconButton>
  );
};

export { ToggleWishlist };
